import React, { useState, useEffect, useCallback } from 'react';
import { apiJson } from '../api';
import { colors, theme } from '../styles';

export default function Dashboard({ onSelectAccount }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [filterAccount, setFilterAccount] = useState('');

  const load = useCallback(async () => {
    const d = await apiJson(`/dashboard?days=${days}`);
    if (d) setData(d);
  }, [days]);

  useEffect(() => {
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, [load]);

  if (!data) return <div style={{ color: colors.textDim, padding: 40 }}>Loading dashboard...</div>;

  const { accounts, analytics, recent_sessions, statuses } = data;
  const filtered = filterAccount
    ? analytics.filter(a => a.account_id === parseInt(filterAccount))
    : analytics;

  const totals = filtered.reduce((acc, a) => ({
    likes: acc.likes + (a.total_likes || 0),
    comments: acc.comments + (a.total_comments || 0),
    follows: acc.follows + (a.total_follows || 0),
    unfollows: acc.unfollows + (a.total_unfollows || 0),
    errors: acc.errors + (a.total_errors || 0),
    actions: acc.actions + (a.total_actions || 0),
  }), { likes: 0, comments: 0, follows: 0, unfollows: 0, errors: 0, actions: 0 });

  const getStatus = (id) => statuses.find(s => s.account_id === id) || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#fff', fontSize: 22, margin: 0 }}>Dashboard</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select style={{ ...theme.select, width: 180 }} value={filterAccount}
            onChange={e => setFilterAccount(e.target.value)}>
            <option value="">All Accounts</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>@{a.username} ({a.platform_name})</option>
            ))}
          </select>
          {[7, 14, 30].map(d => (
            <button key={d} style={{
              ...theme.btnSmall,
              background: days === d ? colors.accent : colors.bgInput,
              color: days === d ? '#fff' : colors.textDim,
              border: `1px solid ${days === d ? colors.accent : colors.borderLight}`,
            }} onClick={() => setDays(d)}>{d}d</button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 20 }}>
        <MetricCard label="Total Actions" value={totals.actions} color={colors.accent} />
        <MetricCard label="Likes" value={totals.likes} color={colors.red} />
        <MetricCard label="Comments" value={totals.comments} color={colors.blue} />
        <MetricCard label="Follows" value={totals.follows} color={colors.green} />
        <MetricCard label="Unfollows" value={totals.unfollows} color={colors.orange} />
        <MetricCard label="Errors" value={totals.errors} color={totals.errors > 0 ? colors.red : colors.textDimmer} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* Account status cards */}
        <div style={theme.card}>
          <h3 style={theme.sectionTitle}>Account Status</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {accounts.map(acc => {
              const st = getStatus(acc.id);
              const isRunning = st.running || acc.running;
              const needsLogin = acc.manual_login && !isRunning;
              const accAnalytics = analytics.find(a => a.account_id === acc.id);

              return (
                <div key={acc.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  background: colors.bgInput, borderRadius: 6,
                  borderLeft: `3px solid ${isRunning ? colors.green : acc.paused ? colors.orange : needsLogin ? colors.yellow : colors.textDimmer}`,
                  cursor: 'pointer',
                }} onClick={() => onSelectAccount(acc)}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: isRunning ? colors.green : acc.paused ? colors.orange : colors.textDimmer,
                  }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>@{acc.username}</span>
                      <span style={{ fontSize: 10, color: colors.textDim }}>{acc.platform_name}</span>
                      {isRunning && <span style={theme.badge(colors.green)}>{st.continuous ? 'Continuous' : 'Running'}</span>}
                      {acc.paused && <span style={theme.badge(colors.orange)}>Paused</span>}
                      {needsLogin && <span style={theme.badge(colors.yellow)}>Needs Login</span>}
                    </div>
                    {st.current_action && (
                      <div style={{ fontSize: 10, color: colors.accent, marginTop: 2 }}>{st.current_action}</div>
                    )}
                  </div>
                  {accAnalytics && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <MiniStat label="L" value={accAnalytics.total_likes} color={colors.red} />
                      <MiniStat label="C" value={accAnalytics.total_comments} color={colors.blue} />
                      <MiniStat label="F" value={accAnalytics.total_follows} color={colors.green} />
                    </div>
                  )}
                </div>
              );
            })}
            {accounts.length === 0 && (
              <div style={{ color: colors.textDim, fontSize: 12, padding: 12, textAlign: 'center' }}>
                No accounts configured yet.
              </div>
            )}
          </div>
        </div>

        {/* Recent sessions */}
        <div style={theme.card}>
          <h3 style={theme.sectionTitle}>Recent Sessions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {recent_sessions.map(s => {
              const statusColor = s.status === 'completed' ? colors.green :
                                  s.status === 'failed' ? colors.red :
                                  s.status === 'running' ? colors.accent : colors.orange;
              return (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  background: colors.bgInput, borderRadius: 4,
                  borderLeft: `2px solid ${statusColor}`,
                }}>
                  <span style={{ color: colors.textMuted, fontSize: 11, minWidth: 80 }}>@{s.username}</span>
                  <span style={{ color: colors.textDimmer, fontSize: 10, minWidth: 90 }}>
                    {new Date(s.started_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <div style={{ display: 'flex', gap: 6, flex: 1, justifyContent: 'center' }}>
                    <MiniStat label="L" value={s.total_likes} color={colors.red} />
                    <MiniStat label="C" value={s.total_comments} color={colors.blue} />
                    <MiniStat label="F" value={s.total_follows} color={colors.green} />
                    <MiniStat label="U" value={s.total_unfollows} color={colors.orange} />
                  </div>
                  <span style={theme.badge(statusColor)}>{s.status}</span>
                </div>
              );
            })}
            {recent_sessions.length === 0 && (
              <div style={{ color: colors.textDim, fontSize: 12, padding: 12, textAlign: 'center' }}>
                No sessions yet.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-account breakdown */}
      {filtered.length > 0 && (
        <div style={{ ...theme.card, marginTop: 16 }}>
          <h3 style={theme.sectionTitle}>Per-Account Breakdown ({days} days)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={thStyle}>Account</th>
                <th style={thStyle}>Platform</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Likes</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Comments</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Follows</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Unfollows</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Errors</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.border}11` }}>
                  <td style={tdStyle}><span style={{ color: '#fff', fontWeight: 500 }}>@{a.username}</span></td>
                  <td style={tdStyle}><span style={theme.badge(colors.accent)}>{a.platform}</span></td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: colors.red }}>{a.total_likes}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: colors.blue }}>{a.total_comments}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: colors.green }}>{a.total_follows}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: colors.orange }}>{a.total_unfollows}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: a.total_errors > 0 ? colors.red : colors.textDimmer }}>{a.total_errors}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#fff', fontWeight: 600 }}>{a.total_actions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <div style={{
      background: colors.bgCard, borderRadius: 8, padding: '16px 12px',
      textAlign: 'center', border: `1px solid ${colors.border}`,
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: colors.textDim, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
    </div>
  );
}

function MiniStat({ label, value, color }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <span style={{ fontSize: 9, color: colors.textDimmer }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: value > 0 ? color : colors.textDimmer }}>{value || 0}</span>
    </span>
  );
}

const thStyle = { padding: '8px 6px', textAlign: 'left', fontSize: 10, color: colors.textDim, textTransform: 'uppercase', letterSpacing: 1 };
const tdStyle = { padding: '8px 6px', fontSize: 13 };
